import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  CompanionAction,
  CompanionHistoryResponse,
  CompanionMessageView,
  CompanionProposal,
  CompanionStreamEvent,
  CompanionView,
} from '@homework/shared';
import { api, ApiError, request, streamEvents } from './api';

/**
 * State for the personal companion, shared by the floating character, its
 * chat panel and the little figure that follows you round the office map.
 *
 * The conversation itself lives on the server; this only mirrors today's page
 * and the reply that is streaming in.
 */

/** What the character is doing, which drives how it moves. */
export type CompanionMood = 'idle' | 'thinking' | 'talking' | 'happy';

/** Vault items the person has pointed Mochi at ("what's this about?"). */
export interface CompanionAttachment {
  type: 'file' | 'folder';
  id: string;
  name: string;
}

/** Fired after a confirmed suggestion changed the Vault, so open views refresh. */
export const VAULT_CHANGED_EVENT = 'homework:vault-changed';

interface CompanionContextValue {
  companion: CompanionView | null;
  messages: CompanionMessageView[];
  loading: boolean;
  loadError: Error | null;
  reload: () => void;

  /** The reply as it streams in; null when nothing is in flight. */
  draft: string | null;
  /** What it is looking at right now ("Checking your calendar"). */
  activity: string | null;
  /** A problem with the last message, in the companion's own words. */
  replyError: string | null;
  sending: boolean;
  mood: CompanionMood;

  /** Resolves true once a reply has landed; false leaves the text to retry. */
  send: (text: string) => Promise<boolean>;
  rename: (name: string) => Promise<void>;
  repaint: (hue: number) => Promise<void>;
  startOver: () => Promise<void>;
  /** Carries out a suggestion through its ordinary endpoint, then records it. */
  confirm: (proposal: CompanionProposal) => Promise<void>;
  dismiss: (proposal: CompanionProposal) => Promise<void>;
  /** A short celebratory hop, e.g. after being named. */
  cheer: () => void;

  /** Whether the chat panel is open. */
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Files or a folder riding along with the next messages, as context. */
  attachments: CompanionAttachment[];
  removeAttachment: (id: string) => void;
  /**
   * Opens the panel about specific Vault items, optionally asking something
   * straight away — "Ask Mochi" buttons across the Vault use this.
   */
  askAbout: (items: CompanionAttachment[], question?: string) => void;
}

const CompanionContext = createContext<CompanionContextValue | null>(null);

/** Labels the Vault's activity entry; grants nothing. */
const VIA_COMPANION = { headers: { 'X-Via-Companion': '1' } };

/** The one place a suggestion turns into a real change — through the normal API. */
async function perform(action: CompanionAction): Promise<void> {
  switch (action.kind) {
    case 'CREATE_FOLDER':
      await request('/vault/folders', { method: 'POST', body: { name: action.name, parentId: action.parentId }, ...VIA_COMPANION });
      return;
    case 'MOVE_ITEM':
      await request('/vault/move', {
        method: 'POST',
        body: { itemType: action.itemType, itemId: action.itemId, targetFolderId: action.targetFolderId },
        ...VIA_COMPANION,
      });
      return;
    case 'RENAME_ITEM':
      await request(
        action.itemType === 'file' ? `/vault/files/${encodeURIComponent(action.itemId)}` : `/vault/folders/${encodeURIComponent(action.itemId)}`,
        { method: 'PATCH', body: { name: action.newName }, ...VIA_COMPANION },
      );
      return;
    case 'SAVE_DOCUMENT':
      await request('/vault/documents', {
        method: 'POST',
        body: { name: action.name, content: action.content, folderId: action.folderId },
        ...VIA_COMPANION,
      });
      return;
    case 'ATTEND_MEETING':
      await api.post('/meetings/notes', { roomId: action.roomId, focus: action.focus ?? undefined });
      return;
    case 'EDIT_DOCUMENT':
      await request(`/vault/files/${encodeURIComponent(action.fileId)}/text`, {
        method: 'PUT',
        body: { content: action.content, baseVersion: action.baseVersion },
        ...VIA_COMPANION,
      });
      return;
    case 'SHARE_ITEM':
      await request('/vault/shares', {
        method: 'POST',
        body: {
          itemType: action.itemType,
          itemId: action.itemId,
          granteeType: action.granteeType,
          granteeId: action.granteeId,
          role: action.role,
        },
        ...VIA_COMPANION,
      });
      return;
    case 'SET_STATUS':
      await api.patch('/me/status', { status: action.status, statusMessage: action.statusMessage ?? null });
      return;
    case 'FOCUS_TASK':
      await api.post(`/tasks/${encodeURIComponent(action.taskId)}/focus`);
      return;
    case 'SET_MANUAL_TASK':
      await api.patch('/me/task', { manualTask: action.manualTask });
      return;
    case 'REQUEST_LEAVE':
      await api.post('/leave/requests', {
        leaveType: action.leaveType,
        startDate: action.startDate,
        endDate: action.endDate,
        dayPart: action.dayPart ?? 'FULL',
        note: action.note ?? null,
      });
      return;
  }
}

function replaceProposal(
  messages: CompanionMessageView[],
  proposalId: string,
  next: CompanionProposal,
): CompanionMessageView[] {
  return messages.map((message) =>
    message.proposals.some((proposal) => proposal.id === proposalId)
      ? { ...message, proposals: message.proposals.map((proposal) => (proposal.id === proposalId ? next : proposal)) }
      : message,
  );
}

export function CompanionProvider({ children }: { children: ReactNode }) {
  const [companion, setCompanion] = useState<CompanionView | null>(null);
  const [messages, setMessages] = useState<CompanionMessageView[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [activity, setActivity] = useState<string | null>(null);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [cheering, setCheering] = useState(false);
  const cheerTimer = useRef<number | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);
  const [open, setOpen] = useState(false);
  const [attachments, setAttachments] = useState<CompanionAttachment[]>([]);
  const pendingQuestion = useRef<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const history = await api.get<CompanionHistoryResponse>('/companion');
      setCompanion(history.companion);
      setMessages(history.messages);
    } catch (error) {
      setLoadError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    return () => {
      abortRef.current?.abort();
      window.clearTimeout(cheerTimer.current);
    };
  }, [load]);

  const cheer = useCallback(() => {
    setCheering(true);
    window.clearTimeout(cheerTimer.current);
    cheerTimer.current = window.setTimeout(() => setCheering(false), 1600);
  }, []);

  const send = useCallback(
    async (text: string): Promise<boolean> => {
      const trimmed = text.trim();
      if (!trimmed || sending) return false;
      setSending(true);
      setReplyError(null);
      setActivity(null);
      setDraft('');
      const controller = new AbortController();
      abortRef.current = controller;
      let delivered = false;
      let replied = false;
      try {
        await streamEvents<CompanionStreamEvent>(
          '/companion/messages',
          {
            text: trimmed,
            ...(attachments.length > 0
              ? {
                  context: {
                    fileIds: attachments.filter((item) => item.type === 'file').map((item) => item.id),
                    folderId: attachments.find((item) => item.type === 'folder')?.id ?? null,
                  },
                }
              : {}),
          },
          (event) => {
            switch (event.type) {
              case 'user':
                delivered = true;
                setMessages((previous) => [...previous, event.message]);
                break;
              case 'delta':
                setActivity(null);
                setDraft((previous) => (previous ?? '') + event.text);
                break;
              case 'activity':
                setActivity(event.label);
                break;
              case 'done':
                replied = true;
                setMessages((previous) => [...previous, event.message]);
                setDraft(null);
                setActivity(null);
                if (event.message.proposals.length > 0) cheer();
                break;
              case 'error':
                setReplyError(event.message);
                break;
            }
          },
          controller.signal,
        );
      } catch (error) {
        if (controller.signal.aborted) return replied;
        setReplyError(
          error instanceof ApiError ? error.message : 'I couldn’t reach the office just now. Try again in a moment?',
        );
      } finally {
        setSending(false);
        setDraft(null);
        setActivity(null);
        abortRef.current = null;
      }
      // The pending copy of what they typed is replaced by the stored one.
      if (delivered) {
        try {
          const history = await api.get<CompanionHistoryResponse>('/companion');
          setMessages(history.messages);
        } catch {
          // Keep the optimistic view; the next open reloads it.
        }
      }
      // True only when a reply landed, so the input keeps the text otherwise.
      return replied;
    },
    [sending, cheer, attachments],
  );

  const rename = useCallback(
    async (name: string) => {
      const response = await api.patch<{ companion: CompanionView }>('/companion', { name });
      setCompanion(response.companion);
      cheer();
    },
    [cheer],
  );

  const repaint = useCallback(async (hue: number) => {
    // Optimistic: the colour changes the moment a swatch is picked.
    setCompanion((previous) => (previous ? { ...previous, hue } : previous));
    const response = await api.patch<{ companion: CompanionView }>('/companion', { hue });
    setCompanion(response.companion);
  }, []);

  const startOver = useCallback(async () => {
    await api.del('/companion/messages');
    setMessages([]);
    setReplyError(null);
  }, []);

  const record = useCallback(async (proposal: CompanionProposal, outcome: 'CONFIRMED' | 'DISMISSED') => {
    const response = await api.post<{ proposal: CompanionProposal }>(
      `/companion/proposals/${encodeURIComponent(proposal.id)}`,
      { outcome },
    );
    setMessages((previous) => replaceProposal(previous, proposal.id, response.proposal));
  }, []);

  const confirm = useCallback(
    async (proposal: CompanionProposal) => {
      await perform(proposal.action);
      await record(proposal, 'CONFIRMED');
      if (!['SET_STATUS', 'FOCUS_TASK', 'SET_MANUAL_TASK', 'REQUEST_LEAVE'].includes(proposal.action.kind)) {
        window.dispatchEvent(new Event(VAULT_CHANGED_EVENT));
      }
      cheer();
    },
    [record, cheer],
  );

  const dismiss = useCallback((proposal: CompanionProposal) => record(proposal, 'DISMISSED'), [record]);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((previous) => previous.filter((item) => item.id !== id));
  }, []);

  const askAbout = useCallback((items: CompanionAttachment[], question?: string) => {
    setAttachments(items.slice(0, 10));
    pendingQuestion.current = question ?? null;
    setOpen(true);
  }, []);

  // A question asked from a Vault button is sent once the attachments it is
  // about are in state — and only once the companion has a name to answer with.
  useEffect(() => {
    const question = pendingQuestion.current;
    if (!open || !question || sending || !companion?.name || !companion.aiAvailable) return;
    pendingQuestion.current = null;
    void send(question);
  }, [open, attachments, companion, sending, send]);

  const mood: CompanionMood = cheering
    ? 'happy'
    : draft
      ? 'talking'
      : sending
        ? 'thinking'
        : 'idle';

  const value = useMemo<CompanionContextValue>(
    () => ({
      companion,
      messages,
      loading,
      loadError,
      reload: () => void load(),
      draft,
      activity,
      replyError,
      sending,
      mood,
      send,
      rename,
      repaint,
      startOver,
      confirm,
      dismiss,
      cheer,
      open,
      setOpen,
      attachments,
      removeAttachment,
      askAbout,
    }),
    [companion, messages, loading, loadError, load, draft, activity, replyError, sending, mood, send, rename, repaint, startOver, confirm, dismiss, cheer, open, attachments, removeAttachment, askAbout],
  );

  return <CompanionContext.Provider value={value}>{children}</CompanionContext.Provider>;
}

export function useCompanion(): CompanionContextValue {
  const value = useContext(CompanionContext);
  if (!value) throw new Error('useCompanion must be used inside <CompanionProvider>');
  return value;
}

/** For the office map, which may render outside the provider in tests. */
export function useOptionalCompanion(): CompanionContextValue | null {
  return useContext(CompanionContext);
}
